import { useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useOrderedCol } from '../lib/hooks';
import { useCurrentCourse } from '../lib/course';
import { toast } from '../components/ui';
import { LogoMark } from '../components/LogoMark';
import type { CourseDoc, ChapterDoc, ResourceDoc } from '../lib/data-model';

/**
 * First-run setup shown right after a user signs in for the first time
 * (design.html "STEP 1 OF 1 · SET UP YOUR HUB"). Writes the user's chosen
 * display name + course pick to their users/{uid} doc and marks it onboarded.
 */
export function OnboardingScreen({ onDone }: { onDone: () => void }) {
  const [, setCourseId] = useCurrentCourse();
  const courses = useOrderedCol<CourseDoc>('courses');
  const chapters = useOrderedCol<ChapterDoc>('chapters');
  const resources = useOrderedCol<ResourceDoc>('resources');

  const googleName = (auth.currentUser?.displayName ?? '').trim();
  const emailPart = (auth.currentUser?.email ?? '').split('@')[0];
  const [name, setName] = useState(googleName || emailPart);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const visibleCourses = useMemo(() => courses.data.filter(c => !c.hidden), [courses.data]);

  const stats = (courseId: string) => ({
    ch: chapters.data.filter(c => !c.hidden && c.courseId === courseId).length,
    rs: resources.data.filter(r => !r.hidden && r.courseId === courseId).length
  });

  const confirm = async () => {
    const u = auth.currentUser;
    const finalName = name.trim();
    if (!u || !finalName || !picked) return;
    setBusy(true);
    try {
      const now = Date.now();
      await setDoc(
        doc(db, 'users', u.uid),
        {
          email: u.email ?? '',
          name: finalName,
          courseKey: picked,
          joinedAt: now,
          lastActive: now,
          onboardedAt: now
        },
        { merge: true }
      );
      setCourseId(picked);
      toast(`Welcome to MasterStax, ${finalName.split(' ')[0]}!`, 'ok');
      onDone();
    } catch (e) {
      toast(`Could not save your setup: ${e instanceof Error ? e.message : String(e)}`, 'err');
      setBusy(false);
    }
  };

  return (
    <div className="welcome">
      <div className="w-orb g" aria-hidden />
      <div className="w-orb p" aria-hidden />

      <div className="onb-card page-anim">
        <div className="onb-brand">
          <span className="logo" aria-hidden><LogoMark /></span>
          <span className="onb-step">STEP 1 OF 1 · SET UP YOUR HUB</span>
        </div>

        <h1 className="onb-title">Welcome. Let's get you into the right course.</h1>
        <p className="onb-lede">
          Your name shows on your progress. You can switch courses any time from your profile.
        </p>

        <div className="onb-field">
          <label htmlFor="onb-name" style={{ display: 'block', fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink3)', fontWeight: 600, marginBottom: 11 }}>
            What should we call you?
          </label>
          <input
            id="onb-name"
            className="onb-input"
            value={name}
            maxLength={60}
            placeholder="Your name"
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="onb-subhead">Pick your course</div>
        <div className="onb-courses">
          {visibleCourses.map(c => {
            const on = picked === c.id;
            const s = stats(c.id);
            return (
              <button
                key={c.id}
                className="onb-course"
                aria-pressed={on}
                style={{ borderColor: on ? 'var(--ink)' : 'var(--line)', background: on ? (c.tint ?? 'var(--surface)') : 'var(--surface)' }}
                onClick={() => setPicked(c.id)}
              >
                <span
                  aria-hidden
                  style={{
                    width: 46, height: 46, borderRadius: 14,
                    background: c.tint ?? 'var(--t-green)', color: c.deep ?? 'var(--c-green)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 600
                  }}
                >
                  {c.glyph}
                </span>
                <span className="mono muted" style={{ fontSize: 11, letterSpacing: '.08em' }}>{c.code}</span>
                <span style={{ fontFamily: 'var(--font-serif)', fontSize: 21, fontWeight: 600, letterSpacing: '-.01em' }}>{c.name}</span>
                <span style={{ fontSize: 14, color: 'var(--ink2)', lineHeight: 1.5 }}>{c.tagline}</span>
                <span className="mono muted" style={{ fontSize: 11.5, marginTop: 'auto', paddingTop: 8 }}>
                  {s.ch} {s.ch === 1 ? 'chapter' : 'chapters'} · {s.rs} {s.rs === 1 ? 'resource' : 'resources'}
                </span>
              </button>
            );
          })}
          {!courses.loading && visibleCourses.length === 0 && <p className="muted">No courses published yet.</p>}
        </div>

        <div className="onb-foot">
          <button
            className="btn btn-dark btn-lg"
            disabled={!picked || !name.trim() || busy}
            onClick={() => void confirm()}
          >
            {busy ? 'Saving…' : !name.trim() ? 'Add your name to continue' : !picked ? 'Pick a course to continue' : 'Enter my hub →'}
          </button>
          <span className="mono muted" style={{ fontSize: 12 }}>More courses are added each term.</span>
        </div>
      </div>
    </div>
  );
}
